import classNames from 'classnames';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { FiCheck, FiX } from 'react-icons/fi';

import { ControllerStyles } from '../../../../components/ControllerStyles';
import { Knob } from '../../../../components/Knob';
import { Slider } from '../../../../components/Slider';
import { audioContext } from '../../../../webAudio/audioContext';
import { filterTypes } from '../../constants';
import { mixer } from '../../mixer';
import { getEqCurvePath } from './getEqCurvePath';
import { controllerValuesEqual } from '../../../../util/controllerValuesEqual';
import { filterTypeOrder } from '../../../../util/constants';
import { getHighlightedClassName } from '../../../../util/getHighlightedClassName';
import { useCanvasSize } from '../../../../hooks/useCanvasSize';

const EqOuterWrapper = styled.div`
   width: calc(var(--safe-width) - 2 * 10px);
   max-width: 800px;
   margin: 0 auto;
   overflow: visible;
`;

const EqWrapper = styled.div`
   display: flex;
   align-items: center;
   touch-action: none;
   -webkit-tap-highlight-color: transparent;
   height: var(--sequencer-zone-height);
   max-height: 500px;

   &.eq-spectrum-above {
      flex-direction: column-reverse;
   }

   .eq-controls {
      overflow: visible;
   }
`;

const VisualizerWrapper = styled.div`
   display: inline-block;
   position: relative;
   border: 2px solid;
   border-radius: 4px;
   box-sizing: content-box;

   & > canvas,
   & > svg {
      position: absolute;
      top: 0;
      left: 0;
   }

   path {
      stroke: var(--text-color);
   }

   &.visualizer-reference {
      canvas,
      path {
         opacity: 0.33;
      }
   }
`;

const nextFilterType = (
   type,
   enabledOptions = [
      filterTypes.peaking,
      filterTypes.notch,
      filterTypes.bandpass,
      filterTypes.lowpass,
      filterTypes.highpass,
      filterTypes.lowshelf,
      filterTypes.highshelf,
   ]
) => {
   const sorted = [...enabledOptions].sort((typeA, typeB) =>
      filterTypeOrder[typeA] > filterTypeOrder[typeB] ? 1 : -1
   );
   const currentTypeIndex = sorted.findIndex(
      (optionType) => type === optionType
   );
   return sorted[(currentTypeIndex + 1) % enabledOptions.length];
};

const mapFilterType = (type) => {
   switch (type) {
      case filterTypes.peaking:
         return 'PK';
      case filterTypes.notch:
         return 'NT';
      case filterTypes.bandpass:
         return 'BP';
      case filterTypes.lowpass:
         return 'LP';
      case filterTypes.highpass:
         return 'HP';
      case filterTypes.lowshelf:
         return 'LS';
      case filterTypes.highshelf:
         return 'HS';
      default:
         return null;
   }
};

const spectrumPositions = { adjacent: 'adjacent', above: 'above' };
const knobPositions = { adjacent: 'adjacent', below: 'below' };

const drawSpectrum = (
   canvasContext,
   canvasWidth,
   canvasHeight,
   minDB,
   maxDB,
   slope,
   bufferLength,
   dataArray,
   sampleRate
) => {
   let barWidth;
   let barHeight;
   let x = 0; // pixel on x-axis of canvas
   let startFreq = 0; // start of current band's frequency range
   let stopFreq; // end of current band's frequency range

   for (let i = 0; i < bufferLength; i += 1) {
      barHeight =
         ((canvasHeight * 1) / (maxDB - minDB)) *
         (dataArray[i] + slope * Math.log2(i + 1) - minDB);

      stopFreq = startFreq + sampleRate / (2 * bufferLength);

      if (startFreq === 0) x = 0;
      else x = (canvasWidth * Math.log2(startFreq / 20)) / 10 - 1; // 20hz to 20480hz

      barWidth = (canvasWidth * Math.log2(stopFreq / 20)) / 10 - x + 1;
      canvasContext.fillRect(x, canvasHeight - barHeight, barWidth, barHeight);
      startFreq = stopFreq;
   }
};

const Spectrum = ({
   instrumentIndex,
   effectIndex,
   animate: shouldAnimate,
   displayReference,
   width,
   height,
   playing,
}) => {
   // Use useRef for mutable variables that we want to persist
   // without triggering a re-render on their change
   const animateRef = React.useRef();
   const shouldAnimateRef = React.useRef();
   const displayReferenceRef = React.useRef();
   const canvasRef = React.useRef();
   const playingRef = React.useRef();

   const animate = () => {
      if (shouldAnimateRef.current) {
         const canvasContext = canvasRef.current.getContext('2d');
         const canvasWidth = width;
         const canvasHeight = height;
         canvasContext.clearRect(0, 0, canvasWidth, canvasHeight);

         canvasContext.fillStyle = '#00898a';

         const spectrumAnalyzer =
            mixer.tracks[instrumentIndex].track.effects.effects[effectIndex]
               .analyzer;

         const analyzerData = new Float32Array(
            spectrumAnalyzer.frequencyBinCount
         );

         spectrumAnalyzer.getFloatFrequencyData(analyzerData);

         let referenceAnalyzerData;
         if (mixer.tracks[instrumentIndex].referenceTrack) {
            const referenceSpectrumAnalyzer =
               mixer.tracks[instrumentIndex].referenceTrack.effects.effects[
                  effectIndex
               ].analyzer;

            referenceAnalyzerData = new Float32Array(
               referenceSpectrumAnalyzer.frequencyBinCount
            );

            referenceSpectrumAnalyzer.getFloatFrequencyData(
               referenceAnalyzerData
            );
         }

         drawSpectrum(
            canvasContext,
            canvasWidth,
            canvasHeight,
            -78,
            0,
            3,
            analyzerData.length,
            displayReferenceRef.current ? referenceAnalyzerData : analyzerData,
            audioContext.sampleRate
         );
      } else {
         const canvasContext = canvasRef.current.getContext('2d');
         const canvasWidth = width;
         const canvasHeight = height;
         canvasContext.clearRect(0, 0, canvasWidth, canvasHeight);

         if (playingRef.current) {
            canvasContext.fillStyle = '#00898a';

            const spectrumAnalyzer =
               mixer.tracks[instrumentIndex].track.effects.effects[effectIndex]
                  .analyzer;

            const analyzerData = new Float32Array(
               spectrumAnalyzer.frequencyBinCount
            );

            drawSpectrum(
               canvasContext,
               canvasWidth,
               canvasHeight,
               0,
               2,
               0,
               analyzerData.length,
               analyzerData.map(
                  (v, i) =>
                     (Math.sin(
                        (Date.now() / 2000) * Math.PI - Math.log2(i + 1) / 2
                     ) +
                        1) /
                     2
               ),
               audioContext.sampleRate
            );
         }
      }
      animateRef.current = requestAnimationFrame(animate);
   };

   React.useEffect(() => {
      animateRef.current = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(animateRef.current);
   }, [width, height]);

   React.useEffect(() => {
      shouldAnimateRef.current = shouldAnimate;
   }, [shouldAnimate]);

   React.useEffect(() => {
      playingRef.current = playing;
   }, [playing]);

   React.useEffect(() => {
      displayReferenceRef.current = displayReference;
   }, [displayReference]);

   return (
      <canvas ref={canvasRef} width={`${width}px`} height={`${height}px`} />
   );
};

const Eq = ({
   settings,
   referenceSettings,
   onUpdate,
   instrumentIndex,
   effectIndex,
   compareAnswer,
   referenceMixer,
   highlightedControls,
   suggestedValues,
   playing,
}) => {
   const graphic = false;

   const { width: canvasWidth, height: canvasHeight } = useCanvasSize(
      'eq-canvas',
      'eq-canvas-wrapper'
   );

   const eqSizes = useMemo(() => {
      // Regular (adjacent/below) Layout defaults
      const svgSize = 16;
      let bandControlsWidth = 40;
      let bandControlsGap = 20;
      const bandControlsInnerGap = 10;
      const controlVerticalPadding = 15;
      const svgPadding = 6;
      const typeHeight = 30;
      const knobOffset = 3;
      const knobDiameter = bandControlsWidth - 2 * knobOffset;
      const typeSectionHeight = typeHeight + controlVerticalPadding;
      const freqSectionHeight = knobDiameter + controlVerticalPadding;
      const qSectionHeight = knobDiameter;
      let gainSectionHeight;
      let gainHeight;

      let typeSectionTop;
      let gainSectionTop;
      let freqSectionTop;
      let qSectionTop;

      let gainLeftOffset = 0;
      let bandWidth = bandControlsWidth;
      const spectrumMarginLeftOrBottom = 10;
      const spectrumBorder = 2;
      let spectrumHeight;

      const estimatedBandWidth =
         canvasHeight > 300
            ? bandWidth
            : (graphic ? 1 : 2) * bandControlsWidth + bandControlsInnerGap;
      const estimatedSpectrumWidthAvailable =
         canvasWidth -
         (settings.bands.length * (estimatedBandWidth + bandControlsGap) -
            bandControlsGap +
            2 * svgPadding) -
         2 * spectrumBorder -
         spectrumMarginLeftOrBottom;

      const spectrumPosition =
         estimatedSpectrumWidthAvailable < 225
            ? spectrumPositions.above
            : spectrumPositions.adjacent;

      if (spectrumPosition === spectrumPositions.adjacent) {
         // svgPadding - 1 to account for svg stroke going into svgPadding
         spectrumHeight =
            canvasHeight - 2 * (svgPadding - 1) - 2 * spectrumBorder;
      } else {
         spectrumHeight = canvasHeight * 0.25;
      }

      const controlsHeight =
         spectrumPosition === spectrumPositions.adjacent
            ? canvasHeight
            : canvasHeight - spectrumHeight - spectrumMarginLeftOrBottom;

      // set as below for graphic because there is no additional horizontal spacce needed for knobs
      const knobsPosition =
         graphic || controlsHeight > 300
            ? knobPositions.below
            : knobPositions.adjacent;

      if (graphic) {
         const visibleBands = settings.bands.length;
         const requiredBandWidthInclcudingGap =
            (canvasWidth - 2 * svgPadding) / visibleBands;

         if (requiredBandWidthInclcudingGap < 60) {
            bandControlsGap = 15;
         }
         if (requiredBandWidthInclcudingGap < 55) {
            bandControlsWidth = 35;
         }
         if (requiredBandWidthInclcudingGap < 50) {
            bandControlsGap = 10;
         }
         if (requiredBandWidthInclcudingGap < 45) {
            bandControlsWidth = 30;
         }
         if (requiredBandWidthInclcudingGap < 40) {
            bandControlsGap = 5;
         }
         if (requiredBandWidthInclcudingGap < 35) {
            bandControlsWidth = 25;
         }

         bandWidth = bandControlsWidth;
      }

      if (knobsPosition === knobPositions.below) {
         gainSectionHeight = graphic
            ? controlsHeight - 2 * svgPadding
            : controlsHeight -
              typeSectionHeight -
              freqSectionHeight -
              qSectionHeight -
              2 * svgPadding;
         gainHeight = gainSectionHeight - controlVerticalPadding;

         typeSectionTop = svgPadding;
         gainSectionTop = graphic
            ? svgPadding
            : typeSectionTop + typeSectionHeight;
         freqSectionTop = gainSectionTop + gainSectionHeight;
         qSectionTop = freqSectionTop + freqSectionHeight;
      }

      if (knobsPosition === knobPositions.adjacent) {
         gainLeftOffset = graphic
            ? 0
            : bandControlsWidth + bandControlsInnerGap;
         bandWidth =
            (graphic ? 1 : 2) * bandControlsWidth + bandControlsInnerGap;

         gainSectionHeight = controlsHeight - 2 * svgPadding;
         gainHeight = gainSectionHeight;
         gainSectionTop = svgPadding;

         typeSectionTop = svgPadding;
         freqSectionTop = typeSectionTop + typeSectionHeight;
         qSectionTop = freqSectionTop + freqSectionHeight;
      }

      const controlsWidth =
         2 * svgPadding +
         settings.bands.length * (bandWidth + bandControlsGap) -
         bandControlsGap;

      let spectrumWidth;

      if (spectrumPosition === spectrumPositions.adjacent) {
         spectrumWidth =
            canvasWidth -
            controlsWidth -
            2 * spectrumBorder -
            spectrumMarginLeftOrBottom;
      } else {
         spectrumWidth = canvasWidth - 2 * spectrumBorder;
      }

      const sliderTrackWidth = bandControlsWidth / 2 - bandControlsWidth / 10;

      return {
         spectrumAbove: spectrumPosition === spectrumPositions.above,
         controlsHeight,
         svgSize,
         bandControlsWidth,
         bandControlsGap,
         controlVerticalPadding,
         svgPadding,
         knobOffset,
         knobDiameter,
         typeHeight,
         gainHeight,
         typeSectionHeight,
         gainSectionHeight,
         freqSectionHeight,
         qSectionHeight,
         typeSectionTop,
         gainSectionTop,
         freqSectionTop,
         qSectionTop,
         bandWidth,
         gainLeftOffset,
         controlsWidth,
         spectrumBorder,
         spectrumMarginLeftOrBottom,
         spectrumWidth,
         spectrumHeight,
         sliderTrackWidth,
      };
   }, [canvasHeight, canvasWidth, settings.bands.length, graphic]);

   const {
      spectrumAbove,
      controlsHeight,
      svgSize,
      bandControlsWidth,
      bandControlsGap,
      svgPadding,
      knobOffset,
      knobDiameter,
      typeHeight,
      gainHeight,
      typeSectionTop,
      gainSectionTop,
      freqSectionTop,
      qSectionTop,
      bandWidth,
      gainLeftOffset,
      controlsWidth,
      spectrumMarginLeftOrBottom,
      spectrumWidth,
      spectrumHeight,
      sliderTrackWidth,
   } = eqSizes;

   if (canvasWidth <= 0 || canvasHeight <= 0) {
      return (
         <EqOuterWrapper id="eq-canvas-wrapper">
            <EqWrapper id="eq-canvas" />
         </EqOuterWrapper>
      );
   }

   return (
      <EqOuterWrapper id="eq-canvas-wrapper">
         <EqWrapper
            onContextMenu={(e) => e.preventDefault()}
            id="eq-canvas"
            className={
               spectrumAbove ? 'eq-spectrum-above' : 'eq-spectrum-adjacent'
            }
         >
            <svg
               height={controlsHeight}
               width={controlsWidth}
               className="eq-controls"
            >
               <ControllerStyles compareAnswer={compareAnswer}>
                  {settings.bands.map(
                     (
                        { type, gain, detune, Q, typeChangeSettings },
                        bandIndex
                     ) => {
                        const referenceBand =
                           referenceSettings?.bands[bandIndex];

                        const typeAndGainCausesChange = !(
                           referenceBand?.gain.value === 0 &&
                           [
                              filterTypes.peaking,
                              filterTypes.lowshelf,
                              filterTypes.highshelf,
                           ].includes(referenceBand?.type.value)
                        );

                        const qMatters =
                           ![
                              filterTypes.lowshelf,
                              filterTypes.highshelf,
                           ].includes(referenceBand?.type.value) &&
                           typeAndGainCausesChange;

                        let filterTypeCorrect =
                           !type.disabled &&
                           referenceBand?.type.value === type.value;

                        if (
                           !type.disabled &&
                           referenceBand?.gain.value === 0 &&
                           [
                              filterTypes.peaking,
                              filterTypes.lowshelf,
                              filterTypes.highshelf,
                           ].includes(referenceBand?.type.value) &&
                           [
                              filterTypes.peaking,
                              filterTypes.lowshelf,
                              filterTypes.highshelf,
                           ].includes(type.value)
                        ) {
                           filterTypeCorrect = true;
                        }

                        return (
                           <g key={bandIndex}>
                              {!graphic && (
                                 <g
                                    className={classNames('click-through', {
                                       correct: filterTypeCorrect,
                                       incorrect:
                                          !type.disabled && !filterTypeCorrect,
                                       disabled: compareAnswer || type.disabled,
                                       ghost: type.disabled,
                                    })}
                                    onClick={() => {
                                       if (compareAnswer || type.disabled) {
                                          return;
                                       }
                                       const nextType = nextFilterType(
                                          type.value,
                                          type.enabledOptions
                                       );

                                       const bandSettings = {
                                          type: {
                                             value: {
                                                $set: nextType,
                                             },
                                          },
                                       };

                                       if (typeChangeSettings?.setValue) {
                                          Object.entries(
                                             typeChangeSettings.setValue
                                          ).forEach(([param, values]) => {
                                             bandSettings[param] = {
                                                value: {
                                                   $set: values[nextType],
                                                },
                                             };
                                          });
                                       }

                                       onUpdate({
                                          bands: {
                                             [bandIndex]: bandSettings,
                                          },
                                       });
                                    }}
                                 >
                                    {highlightedControls[
                                       `track:${instrumentIndex}:effect:${effectIndex}:band:${bandIndex}:type`
                                    ] && (
                                       <rect
                                          x={
                                             svgPadding +
                                             (bandWidth + bandControlsGap) *
                                                bandIndex -
                                             4
                                          }
                                          y={typeSectionTop - 4}
                                          width={bandControlsWidth + 8}
                                          height={typeHeight + 8}
                                          rx={4}
                                          ry={4}
                                          className={classNames(
                                             'click-through-highlight',
                                             {
                                                [getHighlightedClassName(
                                                   highlightedControls[
                                                      `track:${instrumentIndex}:effect:${effectIndex}:band:${bandIndex}:type`
                                                   ]
                                                )]:
                                                   !!highlightedControls[
                                                      `track:${instrumentIndex}:effect:${effectIndex}:band:${bandIndex}:type`
                                                   ],
                                             }
                                          )}
                                       />
                                    )}
                                    <rect
                                       x={
                                          svgPadding +
                                          (bandWidth + bandControlsGap) *
                                             bandIndex
                                       }
                                       y={typeSectionTop}
                                       width={bandControlsWidth}
                                       height={typeHeight}
                                       rx={2}
                                       ry={2}
                                       className={classNames(
                                          'click-through-container',
                                          {
                                             [getHighlightedClassName(
                                                highlightedControls[
                                                   `track:${instrumentIndex}:effect:${effectIndex}:band:${bandIndex}:type`
                                                ]
                                             )]:
                                                !!highlightedControls[
                                                   `track:${instrumentIndex}:effect:${effectIndex}:band:${bandIndex}:type`
                                                ],
                                          }
                                       )}
                                    />
                                    <text
                                       y={typeSectionTop + 15}
                                       x={
                                          (compareAnswer && !type.disabled
                                             ? 6
                                             : 0) +
                                          svgPadding +
                                          bandControlsWidth / 2 +
                                          (bandWidth + bandControlsGap) *
                                             bandIndex
                                       }
                                       textAnchor="middle"
                                       dominantBaseline="central"
                                    >
                                       {mapFilterType(
                                          compareAnswer && referenceMixer
                                             ? referenceSettings?.bands[
                                                  bandIndex
                                               ].type.value
                                             : type.value
                                       )}
                                    </text>

                                    <FiX
                                       className="incorrect-icon"
                                       size={svgSize}
                                       x={
                                          svgPadding +
                                          (bandWidth + bandControlsGap) *
                                             bandIndex
                                       }
                                       y={typeSectionTop + 7}
                                    />

                                    <FiCheck
                                       className="correct-icon"
                                       size={svgSize}
                                       x={
                                          svgPadding +
                                          (bandWidth + bandControlsGap) *
                                             bandIndex
                                       }
                                       y={typeSectionTop + 7}
                                    />
                                 </g>
                              )}
                              <Slider
                                 x={
                                    gainLeftOffset +
                                    svgPadding +
                                    (bandWidth + bandControlsGap) * bandIndex
                                 }
                                 y={gainSectionTop}
                                 height={gainHeight}
                                 width={bandControlsWidth}
                                 trackWidth={sliderTrackWidth}
                                 handleHeight={16}
                                 highlighted={
                                    highlightedControls[
                                       `track:${instrumentIndex}:effect:${effectIndex}:band:${bandIndex}:gain`
                                    ]
                                 }
                                 suggestedValue={
                                    suggestedValues[
                                       `track:${instrumentIndex}:effect:${effectIndex}:band:${bandIndex}:gain`
                                    ]
                                 }
                                 value={
                                    compareAnswer && referenceMixer
                                       ? referenceSettings?.bands[bandIndex]
                                            .gain.value
                                       : gain.value
                                 }
                                 incrementCount={gain.incrementCount}
                                 disabled={compareAnswer || gain.disabled}
                                 ghost={gain.disabled}
                                 tested={
                                    !gain.disabled &&
                                    ![
                                       filterTypes.lowpass,
                                       filterTypes.highpass,
                                       filterTypes.notch,
                                       filterTypes.bandpass,
                                    ].includes(referenceBand?.type.value) &&
                                    ![
                                       filterTypes.lowpass,
                                       filterTypes.highpass,
                                       filterTypes.notch,
                                       filterTypes.bandpass,
                                    ].includes(type.value)
                                 }
                                 correct={controllerValuesEqual(
                                    referenceSettings?.bands[bandIndex].gain
                                       .value,
                                    gain.value
                                 )}
                                 notApplicable={
                                    ([
                                       filterTypes.bandpass,
                                       filterTypes.highpass,
                                       filterTypes.lowpass,
                                       filterTypes.notch,
                                    ].includes(type.value) &&
                                       !(compareAnswer && referenceMixer)) ||
                                    (compareAnswer &&
                                       referenceMixer &&
                                       [
                                          filterTypes.bandpass,
                                          filterTypes.highpass,
                                          filterTypes.lowpass,
                                          filterTypes.notch,
                                       ].includes(referenceBand?.type.value))
                                 }
                                 bipolar
                                 onChange={(value) =>
                                    onUpdate({
                                       bands: {
                                          [bandIndex]: {
                                             gain: { value: { $set: value } },
                                          },
                                       },
                                    })
                                 }
                              />
                              {!graphic && (
                                 <Knob
                                    x={
                                       svgPadding +
                                       (bandWidth + bandControlsGap) *
                                          bandIndex +
                                       knobOffset
                                    }
                                    y={freqSectionTop}
                                    diameter={knobDiameter}
                                    value={
                                       compareAnswer &&
                                       referenceMixer &&
                                       typeAndGainCausesChange
                                          ? referenceSettings?.bands[bandIndex]
                                               .detune.value
                                          : detune.value
                                    }
                                    highlighted={
                                       highlightedControls[
                                          `track:${instrumentIndex}:effect:${effectIndex}:band:${bandIndex}:detune`
                                       ]
                                    }
                                    suggestedValue={
                                       suggestedValues[
                                          `track:${instrumentIndex}:effect:${effectIndex}:band:${bandIndex}:detune`
                                       ]
                                    }
                                    incrementCount={detune.incrementCount}
                                    disabled={compareAnswer || detune.disabled}
                                    ghost={detune.disabled}
                                    tested={
                                       !detune.disabled &&
                                       typeAndGainCausesChange
                                    }
                                    correct={controllerValuesEqual(
                                       referenceSettings?.bands[bandIndex]
                                          .detune.value,
                                       detune.value
                                    )}
                                    onChange={(value) =>
                                       onUpdate({
                                          bands: {
                                             [bandIndex]: {
                                                detune: {
                                                   value: { $set: value },
                                                },
                                             },
                                          },
                                       })
                                    }
                                 />
                              )}
                              {!graphic && (
                                 <Knob
                                    x={
                                       svgPadding +
                                       (bandWidth + bandControlsGap) *
                                          bandIndex +
                                       knobOffset
                                    }
                                    y={qSectionTop}
                                    diameter={knobDiameter}
                                    value={
                                       compareAnswer &&
                                       referenceMixer &&
                                       qMatters
                                          ? referenceSettings?.bands[bandIndex]
                                               .Q.value
                                          : Q.value
                                    }
                                    highlighted={
                                       highlightedControls[
                                          `track:${instrumentIndex}:effect:${effectIndex}:band:${bandIndex}:Q`
                                       ]
                                    }
                                    suggestedValue={
                                       suggestedValues[
                                          `track:${instrumentIndex}:effect:${effectIndex}:band:${bandIndex}:Q`
                                       ]
                                    }
                                    incrementCount={Q.incrementCount}
                                    disabled={compareAnswer || Q.disabled}
                                    ghost={Q.disabled}
                                    tested={
                                       !Q.disabled &&
                                       qMatters &&
                                       ![
                                          filterTypes.lowshelf,
                                          filterTypes.highshelf,
                                       ].includes(type.value)
                                    }
                                    correct={controllerValuesEqual(
                                       referenceSettings?.bands[bandIndex].Q
                                          .value,
                                       Q.value
                                    )}
                                    notApplicable={
                                       ([
                                          filterTypes.lowshelf,
                                          filterTypes.highshelf,
                                       ].includes(type.value) &&
                                          !(compareAnswer && referenceMixer)) ||
                                       (compareAnswer &&
                                          referenceMixer &&
                                          [
                                             filterTypes.lowshelf,
                                             filterTypes.highshelf,
                                          ].includes(referenceBand?.type.value))
                                    }
                                    onChange={(value) =>
                                       onUpdate({
                                          bands: {
                                             [bandIndex]: {
                                                Q: { value: { $set: value } },
                                             },
                                          },
                                       })
                                    }
                                 />
                              )}
                           </g>
                        );
                     }
                  )}
               </ControllerStyles>
            </svg>
            <VisualizerWrapper
               className={classNames({
                  'visualizer-reference': referenceMixer,
               })}
               style={{
                  width: `${spectrumWidth}px`,
                  height: `${spectrumHeight}px`,
                  [spectrumAbove
                     ? 'marginBottom'
                     : 'marginLeft']: `${spectrumMarginLeftOrBottom}px`,
               }}
            >
               <Spectrum
                  instrumentIndex={instrumentIndex}
                  effectIndex={effectIndex}
                  animate={compareAnswer || !referenceMixer}
                  displayReference={compareAnswer && referenceMixer}
                  width={spectrumWidth}
                  height={spectrumHeight}
                  playing={playing}
               />
               <svg width={spectrumWidth} height={spectrumHeight}>
                  <path
                     stroke="black"
                     strokeLinejoin="round"
                     fill="none"
                     strokeWidth="3"
                     d={getEqCurvePath(
                        spectrumWidth,
                        spectrumHeight,
                        audioContext.sampleRate,
                        -78,
                        0,
                        compareAnswer && referenceMixer
                           ? referenceSettings?.bands
                           : settings.bands
                     )}
                  />
               </svg>
            </VisualizerWrapper>
         </EqWrapper>
      </EqOuterWrapper>
   );
};

export { Eq };
